import { useQuery } from '@tanstack/react-query';

import type { IInventoryItem } from '@ekhodealer/ekho-common';

import { useCurrentServicesProviderId } from '@common/hooks/useCurrentServicesProviderId';
import { apiClient } from '@config/server/apiClient';
import { inventoryEndpoints } from '@config/server/apiEndpoints';
import { queries } from '@config/server/queryKeys';

/**
 * API function to get a single inventory item by ID
 */
export const getInventoryItem = async (
	servicesProviderId: string,
	id: string,
): Promise<IInventoryItem> => {
	const response = await apiClient.get<IInventoryItem>(
		inventoryEndpoints.detail(servicesProviderId, id),
	);
	return response.data;
};

/**
 * Get query configuration for an inventory item
 * Can be used with useQuery or useQueries
 */
export const getInventoryItemQueryConfig = (
	servicesProviderId: string,
	id: string,
	enabled = true,
) => ({
	queryKey: queries.inventory.detail(id).queryKey,
	queryFn: () => getInventoryItem(servicesProviderId, id),
	enabled: enabled && !!id && !!servicesProviderId,
	staleTime: Infinity, // never stale unless the data is mutated
});

/**
 * Hook for fetching a single inventory item by ID
 */
export const useGetInventoryItem = (id: string) => {
	const currentServicesProviderId = useCurrentServicesProviderId();

	return useQuery(getInventoryItemQueryConfig(currentServicesProviderId, id));
};
